/**
 * RevisionRequestsSection — explicit revision requests on a ball
 * (revision_requests). A revision request is a Return: the server moves the
 * loop to `returned` with the ball in the middle tier, and when the LAST open
 * request is closed it returns the loop to `middle_working` (migration 713).
 *
 * Closing had no home on any platform before this component. Both actions are
 * authority transitions and go through the RPCs only — never a table write.
 */

import { useMemo, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { useToast } from '../../../ui/Toast';
import { useActorDisplayMap } from '../../../../hooks/prioritization/useActorDisplayMap';
import { createRevisionRequest } from '../../../../lib/commands/createRevisionRequest';
import { closeRevisionRequest } from '../../../../lib/commands/closeRevisionRequest';
import {
  canCloseRevisionRequest,
  canRequestRevision,
  closeResolutionFor,
  countOpen,
  isOpenStatus,
  revisionStatusLabel,
  sortRevisionRequests,
  type LoopPartyFacts,
  type LoopRevisionRequest,
} from './threadLogic';
import {
  GatedButton,
  InlineError,
  PRIMARY_BUTTON_CLASS,
  SECTION_CLASS,
  ThreadComposer,
  formatWhen,
} from './ThreadComposer';

export function RevisionRequestsSection({
  loopId,
  viewerId,
  viewerName,
  loopFacts,
  revisionRequests,
  readError,
  onChanged,
}: {
  loopId: string;
  viewerId: string | null;
  viewerName?: string | null;
  loopFacts: LoopPartyFacts;
  revisionRequests: LoopRevisionRequest[];
  readError: string | null;
  /** Refetch requests AND the loop (its state changes); awaited before success is reported. */
  onChanged: () => Promise<void>;
}) {
  const { showToast } = useToast();
  const [requesting, setRequesting] = useState(false);
  const [closingId, setClosingId] = useState<string | null>(null);

  const sorted = useMemo(() => sortRevisionRequests(revisionRequests), [revisionRequests]);
  const actorIds = useMemo(
    () => revisionRequests.flatMap((r) => [r.requestedBy, r.closedBy]),
    [revisionRequests],
  );
  const names = useActorDisplayMap(actorIds);
  const nameOf = (id: string | null) =>
    !id ? 'Someone' : id === viewerId ? 'You' : names[id] ?? 'A colleague';

  const openCount = countOpen(revisionRequests);
  const requestGate = canRequestRevision(viewerId, loopFacts);

  return (
    <section id="inbox-revisions" className={SECTION_CLASS}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            <RotateCcw className="h-4 w-4 text-[var(--text-ter)]" />
            Revision requests
            {openCount > 0 && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)]">
                {openCount} open
              </span>
            )}
          </h2>
          <p className="text-xs text-[var(--text-ter)] mt-1">
            Requesting a revision returns the ball to the working level. It stays returned until every
            open request is marked addressed.
          </p>
        </div>
        {!requesting && (
          <GatedButton gate={requestGate} onClick={() => setRequesting(true)} className={PRIMARY_BUTTON_CLASS}>
            Request a revision
          </GatedButton>
        )}
      </div>

      <InlineError message={readError ? `Could not refresh revision requests: ${readError}` : null} />

      {requesting && viewerId && (
        <ThreadComposer
          label="Reason"
          placeholder="What needs to change before this can be accepted?"
          submitLabel="Request revision"
          busyLabel="Requesting…"
          onCancel={() => setRequesting(false)}
          onSubmit={async (message) => {
            await createRevisionRequest({ loopId, message, actorId: viewerId, actorName: viewerName ?? null });
            await onChanged();
            setRequesting(false);
            showToast('success', 'Revision requested', 'The ball is returned to the working level.');
          }}
        />
      )}

      {sorted.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)]">No revision requests on this ball.</p>
      ) : (
        <ul className="divide-y divide-[var(--rule)] border-t border-[var(--rule)]">
          {sorted.map((r) => {
            const open = isOpenStatus(r.status);
            const closeGate = canCloseRevisionRequest(r, viewerId, loopFacts);
            const resolution = closeResolutionFor(r, viewerId);
            return (
              <li key={r.id} className="py-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-[var(--text-ter)]">
                    <span className="font-medium text-[var(--text-sec)]">{nameOf(r.requestedBy)}</span> asked for a
                    revision
                    {formatWhen(r.requestedAt) && ` · ${formatWhen(r.requestedAt)}`}
                  </p>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      open
                        ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                        : 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                    }`}
                  >
                    {revisionStatusLabel(r.status)}
                  </span>
                </div>
                <p className="text-sm text-[var(--text-pri)] whitespace-pre-wrap">{r.message}</p>
                {!open && (
                  <p className="text-xs text-[var(--text-ter)]">
                    {revisionStatusLabel(r.status)} by{' '}
                    <span className="font-medium text-[var(--text-sec)]">{nameOf(r.closedBy)}</span>
                    {formatWhen(r.closedAt) && ` · ${formatWhen(r.closedAt)}`}
                  </p>
                )}

                {open && closingId !== r.id && (
                  <GatedButton gate={closeGate} onClick={() => setClosingId(r.id)}>
                    {resolution === 'WITHDRAWN' ? 'Withdraw request' : 'Mark addressed'}
                  </GatedButton>
                )}

                {closingId === r.id && viewerId && (
                  <ThreadComposer
                    label={resolution === 'WITHDRAWN' ? 'Why withdraw (optional)' : 'What was done (optional)'}
                    required={false}
                    submitLabel={resolution === 'WITHDRAWN' ? 'Withdraw request' : 'Mark addressed'}
                    busyLabel="Closing…"
                    onCancel={() => setClosingId(null)}
                    onSubmit={async (note) => {
                      await closeRevisionRequest({
                        revisionRequestId: r.id,
                        resolution,
                        actorId: viewerId,
                        actorName: viewerName ?? null,
                        note: note || null,
                      });
                      await onChanged();
                      setClosingId(null);
                      showToast(
                        'success',
                        resolution === 'WITHDRAWN' ? 'Revision request withdrawn' : 'Revision request marked addressed',
                      );
                    }}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
